/**
 * Request schemas of the B2 part 1 routes (ADR-06 §2.1: zod at the boundary,
 * unknown fields rejected). Amounts are decimal strings.
 */
import { z } from "zod";
import { PERMISSION_TOKENS } from "./permissions.js";

export const usernameSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[\p{L}\p{N}_.-]+$/u, "USERNAME_FORMAT"); // D-130: trimmed; uniqueness is case-insensitive (DB)

// D-167: 12 characters with 2FA, 15 without — checked by the service, which answers PASSWORD_POLICY with `minLength`.
// [C37] F-370: no length rule here, or a short password is refused as a generic VALIDATION ("بيانات غير صالحة") that
// the tester read as a wrong authenticator code.
export const passwordSchema = z.string().min(1).max(256);
export const pinSchema = z.string().regex(/^\d{6}$/, "PIN_FORMAT"); // D-018, D-167: 6 digits
export const totpCodeSchema = z.string().regex(/^\d{6}$/, "TOTP_FORMAT");
export const recoveryCodeSchema = z.string().regex(/^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/i);
export const permissionTokenSchema = z.string().refine((t) => PERMISSION_TOKENS.has(t), "PERMISSION_UNKNOWN");
export const uuidSchema = z.string().uuid();
export const decimalSchema = z.string().regex(/^\d+(\.\d{1,6})?$/, "DECIMAL_STRING");

/** Supervisor PIN on the same screen (D-018, D-133): the supervisor's username + PIN. */
export const supervisorSchema = z.object({ username: usernameSchema, pin: pinSchema }).strict();

/** Every PIN-gated request carries one of these (D-018). */
export const pinProofSchema = z
  .object({
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict();

export const loginSchema = z
  .object({
    username: usernameSchema,
    password: z.string().min(1).max(256),
  })
  .strict();

export const secondFactorSchema = z
  .object({
    code: totpCodeSchema.optional(),
    recoveryCode: recoveryCodeSchema.optional(),
  })
  .strict()
  .refine((v) => (v.code ? 1 : 0) + (v.recoveryCode ? 1 : 0) === 1, "ONE_FACTOR");

export const setupCompleteSchema = z
  .object({
    token: z.string().min(20).max(128),
    password: passwordSchema,
    pin: pinSchema,
    totpCode: totpCodeSchema, // the Super Manager's 2FA is mandatory (D-036): enrolment completes the setup
  })
  .strict();

export const changePinSchema = z.object({ currentPin: pinSchema, newPin: pinSchema }).strict();
export const resetPinSchema = z.object({ userId: uuidSchema, newPin: pinSchema, pin: pinSchema }).strict(); // D-037: manager's own PIN
export const totpEnrolConfirmSchema = z.object({ code: totpCodeSchema, pin: pinSchema }).strict();

export const createUserSchema = z
  .object({
    username: usernameSchema,
    displayName: z.string().trim().min(1).max(120),
    employeeCode: z.string().trim().length(2).optional(),
    roleId: uuidSchema, // the user sets password, PIN and 2FA through the one-time setup link (D-037, D-132)
    branchIds: z.array(uuidSchema).min(1).max(50).optional(),
    pin: pinSchema,
  })
  .strict();

export const updateUserSchema = z
  .object({
    userId: uuidSchema,
    username: usernameSchema.optional(),
    displayName: z.string().trim().min(1).max(120).optional(),
    employeeCode: z.string().trim().length(2).nullable().optional(),
    roleId: uuidSchema.optional(), // D-130: resets grants / revocations to the role's defaults
    grants: z.array(permissionTokenSchema).max(100).optional(),
    revocations: z.array(permissionTokenSchema).max(100).optional(),
    active: z.boolean().optional(), // false = deactivate (D-130); sessions revoked at once
    twoFactorRequired: z.boolean().optional(), // D-036 / D-207 per-account relaxation (Super Manager: always true)
    workingHours: z
      .array(z.object({ weekday: z.number().int().min(0).max(6), from: z.string().regex(/^\d{2}:\d{2}$/), to: z.string().regex(/^\d{2}:\d{2}$/) }).strict())
      .max(21)
      .nullable()
      .optional(), // D-041, D-131 (ranges across midnight allowed)
    limitIqd: decimalSchema.nullable().optional(),
    limitUsd: decimalSchema.nullable().optional(),
    allowedBoxIds: z.array(uuidSchema).nullable().optional(),
    allowedCustodyIds: z.array(uuidSchema).nullable().optional(),
    allowedWarehouseIds: z.array(uuidSchema).nullable().optional(),
    pin: pinSchema,
  })
  .strict();

export const createRoleSchema = z
  .object({ name: z.string().trim().min(2).max(60), permissions: z.array(permissionTokenSchema).max(100), pin: pinSchema })
  .strict();

export const updateRoleSchema = z
  .object({ roleId: uuidSchema, name: z.string().trim().min(2).max(60).optional(), permissions: z.array(permissionTokenSchema).max(100).optional(), pin: pinSchema })
  .strict();

export const setOfficeSettingSchema = z
  .object({
    key: z.string().min(1).max(80),
    value: z.unknown(),
    pin: pinSchema,
  })
  .strict();

export const setPreferenceSchema = z.object({ key: z.string().min(1).max(80), value: z.unknown() }).strict();

/** D-126 + D-018: own PIN, or a supervisor's username + PIN on the same screen (route access `own_or_supervisor`). */
export const setRateSchema = z
  .object({
    currency: z.string().trim().toUpperCase().min(3).max(8),
    buy: decimalSchema,
    sell: decimalSchema,
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict();

export type LoginInput = z.infer<typeof loginSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ---------------------------------------------------------------------------
// Operation gateway (C18, B2 part 2): preview → commit shared by every money
// route (ADR-08 §2.1 item 2, D-131, D-210), second-person approval (D-035,
// D-119), approval rules, notifications (D-138).
// ---------------------------------------------------------------------------
export const operationTypeSchema = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/, "OPERATION_TYPE_FORMAT");

/** Dry run: the engine plans, nothing is posted; the response carries `previewId` + the derived figures. */
/**
 * [C22] A customer account opened **inside** an operation (D-018, D-245 (e)): the input refers to it as
 * `"new:<ref>"`; the gateway creates it in the posting transaction (rolled back after a preview), so the account
 * exists only if the operation is committed. `currency` names which of the account's two ledger lines (IQD / USD)
 * the placeholder resolves to. Either `name` (a new person; refused with CUSTOMER_NAME_EXISTS when the name is
 * taken) or `personId` (the other kind for an existing person, D-017).
 */
export const newCustomerAccountSchema = z
  .object({
    ref: z.string().regex(/^[a-z0-9_]{1,16}$/),
    kind: z.enum(["debt", "trust"]),
    currency: z.enum(["IQD", "USD"]),
    name: z.string().trim().min(1).max(120).optional(),
    personId: uuidSchema.optional(),
    phone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
    accountNumber: z.string().trim().min(1).max(30).optional(),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  })
  .strict()
  .refine((v) => (v.name ? 1 : 0) + (v.personId ? 1 : 0) === 1, { message: "NAME_XOR_PERSON" });
export type NewCustomerAccountInput = z.infer<typeof newCustomerAccountSchema>;

export const previewOperationSchema = z
  .object({
    type: operationTypeSchema,
    input: z.record(z.string(), z.unknown()),
    branchId: uuidSchema.optional(), // default: the session's first branch
    newAccounts: z.array(newCustomerAccountSchema).max(3).optional(), // [C22] D-245 (e)
  })
  .strict();

/** [C22] Several previews committed in one transaction under one PIN (D-246 (c): full settlement of IQD + USD). */
export const commitBatchSchema = z
  .object({
    items: z.array(z.object({ previewId: uuidSchema, idempotencyKey: uuidSchema }).strict()).min(1).max(4),
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict();

/** Commit of a preview: own PIN (or a supervisor's on the same screen, D-018); `idempotencyKey` is client-generated per user action (ADR-02 §2.6). */
export const commitOperationSchema = z
  .object({
    previewId: uuidSchema,
    idempotencyKey: uuidSchema,
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict();

export const approveOperationSchema = z.object({ pendingId: uuidSchema, idempotencyKey: uuidSchema, pin: pinSchema }).strict();
export const rejectOperationSchema = z.object({ pendingId: uuidSchema, reason: z.string().trim().min(1).max(500), pin: pinSchema }).strict();
export const cancelPendingSchema = z.object({ pendingId: uuidSchema, note: z.string().trim().max(500).optional() }).strict();

/** D-035: the Super Manager selects operations that need approval, an optional threshold (IQD equivalent, D-012 unit for USD) and the approvers. */
export const setApprovalRuleSchema = z
  .object({
    operationType: operationTypeSchema,
    thresholdIqd: decimalSchema.nullable().optional(), // null / absent = every operation of the type
    approverUserIds: z.array(uuidSchema).max(50).nullable().optional(), // null / absent = any approve_operations holder
    active: z.boolean().default(true),
    pin: pinSchema,
  })
  .strict();

export const notificationIdSchema = z.object({ id: uuidSchema.optional() }).strict(); // absent = all of mine

export type PreviewOperationInput = z.infer<typeof previewOperationSchema>;
export type CommitOperationInput = z.infer<typeof commitOperationSchema>;
export type SetApprovalRuleInput = z.infer<typeof setApprovalRuleSchema>;

// --- boxes and vaults (C19, B4 start; D04 §5 "Box management", D-074 rates) ---
export const ratePctSchema = z.string().regex(/^\d{1,3}(\.\d{1,4})?$/, "RATE_PCT");
export const createBoxSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    /** Opening balance ≥ 0 posted as `capital_add` (D04 §5); absent or "0" = none. */
    openingBalance: decimalSchema.optional(),
    depositRatePct: ratePctSchema.nullable().optional(),
    withdrawRatePct: ratePctSchema.nullable().optional(),
    idempotencyKey: uuidSchema,
    pin: pinSchema,
  })
  .strict();
export const updateBoxSchema = z
  .object({
    accountId: uuidSchema,
    name: z.string().trim().min(1).max(80).optional(),
    depositRatePct: ratePctSchema.nullable().optional(),
    withdrawRatePct: ratePctSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    pin: pinSchema,
  })
  .strict();
export const archiveBoxSchema = z.object({ accountId: uuidSchema, archive: z.boolean(), pin: pinSchema }).strict();
export const statementQuerySchema = z
  .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), // office dates (D-064)
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    operatorUserId: uuidSchema.optional(),
    direction: z.enum(["in", "out"]).optional(),
    type: operationTypeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
  })
  .strict();
/** [C20] `GET /dollar/operations` (D01 §1 "USD transaction feed with date filter, search"): office dates (D-064), free text over note / operator. */
export const dollarOperationsQuerySchema = z
  .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    q: z.string().trim().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
  })
  .strict();
export type DollarOperationsQuery = z.infer<typeof dollarOperationsQuerySchema>;
/** [C21] `GET /currencies/operations` (D02 §1 "Movement ledger with currency, type, date range and text search"). */
export const currencyOperationsQuerySchema = z
  .object({
    currency: z.string().trim().toUpperCase().min(3).max(8).optional(),
    type: z.enum(["buy", "sell", "deposit", "withdraw"]).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    q: z.string().trim().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
  })
  .strict();
export type CurrencyOperationsQuery = z.infer<typeof currencyOperationsQuerySchema>;
const currencyCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{3,8}$/, "CURRENCY_CODE");
const billsSchema = z.array(decimalSchema).max(20);
/** [C21] Super currencies card — add a currency (D-021; old SuperCurrenciesSettings.tsx:86-175). */
export const addCurrencySchema = z
  .object({
    code: currencyCodeSchema,
    nameAr: z.string().trim().min(1).max(60),
    nameEn: z.string().trim().min(1).max(60),
    symbol: z.string().trim().min(1).max(8),
    /** Units one quoted rate refers to (D-021: e.g. 1, 1000, 1000000); the unit is never guessed (F-070). */
    quoteUnit: z.enum(["1", "10", "100", "1000", "10000", "100000", "1000000"]).default("1"),
    precision: z.number().int().min(0).max(3).default(2),
    multiple: decimalSchema.default("1"),
    bills: billsSchema.default([]),
    buy: decimalSchema,
    sell: decimalSchema,
    pin: pinSchema,
  })
  .strict();
/** [C21] edit, show / hide, suspend (D-069 (3)); the quote unit and precision are fixed after creation (D-161). */
export const updateCurrencySchema = z
  .object({
    code: currencyCodeSchema,
    nameAr: z.string().trim().min(1).max(60).optional(),
    nameEn: z.string().trim().min(1).max(60).optional(),
    symbol: z.string().trim().min(1).max(8).optional(),
    multiple: decimalSchema.optional(),
    bills: billsSchema.optional(),
    visible: z.boolean().optional(),
    suspended: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    pin: pinSchema,
  })
  .strict();
export const deleteCurrencySchema = z.object({ code: currencyCodeSchema, pin: pinSchema }).strict();
export type AddCurrencyInput = z.infer<typeof addCurrencySchema>;
export type UpdateCurrencyInput = z.infer<typeof updateCurrencySchema>;
export type CreateBoxInput = z.infer<typeof createBoxSchema>;
export type UpdateBoxInput = z.infer<typeof updateBoxSchema>;
export type StatementQuery = z.infer<typeof statementQuerySchema>;

// --- customer accounts (C22, B4: D03 §5; D-016 – D-018, D-029, D-049, D-072, D-073; engineer decision D-245) ---
const officeDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const customerNameSchema = z.string().trim().min(1).max(120);
/**
 * Add account (old modal `App.tsx:47513-47840`): a new person by `name`, or the other kind for an existing person
 * (`personId`, D-017); opening amount ≥ 0 (D-016 (d)) through a box (debt = cash out, trust = cash in, D-218 (b));
 * `create_customers` + own PIN, or a supervisor's (D-018).
 */
export const createCustomerSchema = z
  .object({
    name: customerNameSchema.optional(),
    personId: uuidSchema.optional(),
    kind: z.enum(["debt", "trust"]),
    phone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
    accountNumber: z.string().trim().min(1).max(30).optional(), // absent = next number of the office (R-2 "auto-generated")
    dueDate: officeDateSchema.nullable().optional(),
    currency: z.enum(["IQD", "USD"]).default("IQD"),
    amount: decimalSchema.default("0"),
    boxAccountId: uuidSchema.optional(), // required when amount > 0
    custodyUserId: uuidSchema.optional(),
    note: z.string().trim().max(500).optional(),
    branchId: uuidSchema.optional(),
    idempotencyKey: uuidSchema,
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict()
  .refine((v) => (v.name ? 1 : 0) + (v.personId ? 1 : 0) === 1, { message: "NAME_XOR_PERSON" });
/** Edit (old `App.tsx:21908-21976`): `show_customer_delete_edit` + `edit_customers`, own PIN (D-018). The name belongs to the person (both accounts). */
export const updateCustomerSchema = z
  .object({
    id: uuidSchema,
    name: customerNameSchema.optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    address: z.string().trim().max(200).nullable().optional(),
    accountNumber: z.string().trim().min(1).max(30).optional(),
    dueDate: officeDateSchema.nullable().optional(),
    pin: pinSchema,
  })
  .strict();
/** Archive / restore (D-072): `show_customer_delete_edit` + `delete_customers`, own PIN. */
export const archiveCustomerSchema = z.object({ id: uuidSchema, archive: z.boolean(), pin: pinSchema }).strict();
export const customersQuerySchema = z.object({ archived: z.coerce.boolean().default(false) }).strict();
export const customerNameCheckSchema = z.object({ name: customerNameSchema }).strict();
export const customerStatementQuerySchema = z
  .object({
    from: officeDateSchema.optional(),
    to: officeDateSchema.optional(),
    operatorUserId: uuidSchema.optional(),
    type: operationTypeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
  })
  .strict();
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;
export type CustomerStatementQuery = z.infer<typeof customerStatementQuerySchema>;

// ---------------------------------------------------------------------------
// Cash transfers with non-linked partner offices (C23, B4 — D05 §5; D-079 –
// D-088, D-125, D-147; engineer decisions D-248 – D-250). The money is one
// operation of the five D05 types through the gateway; these routes add the
// transfer record (beneficiary, encrypted document, destination, code) and
// keep the document number out of the ledger input.
// ---------------------------------------------------------------------------
export const TRANSFER_TYPES = ["transfer_out", "transfer_in", "transfer_out_settled", "transfer_in_settled", "transfer_currency_passthrough"] as const;
export type TransferType = (typeof TRANSFER_TYPES)[number];

/** The old required fields (CashTransfers.tsx:708-711): person, document, destination, code; the note is new and optional. */
export const transferDetailsSchema = z
  .object({
    beneficiaryName: z.string().trim().min(1).max(120),
    documentId: z.string().trim().min(1).max(60),
    destination: z.string().trim().min(1).max(120),
    code: z.string().trim().min(1).max(40),
    note: z.string().trim().max(300).optional(),
  })
  .strict();
export type TransferDetailsInput = z.infer<typeof transferDetailsSchema>;

export const transferPreviewSchema = z
  .object({
    type: z.enum(TRANSFER_TYPES),
    /** The engine input of the type (`TransferInput` / `SettledTransferInput` / `PassthroughTransferInput`) without `details`. */
    input: z.record(z.string(), z.unknown()),
    details: transferDetailsSchema,
    branchId: uuidSchema.optional(),
    newAccounts: z.array(newCustomerAccountSchema).max(3).optional(),
  })
  .strict();
export type TransferPreviewInput = z.infer<typeof transferPreviewSchema>;

export const transferCommitSchema = z
  .object({
    previewId: uuidSchema,
    idempotencyKey: uuidSchema,
    /** D-087: the operator saw the duplicate-code warning of an incoming transfer and saves anyway (audited). */
    acknowledgeDuplicateCode: z.boolean().optional(),
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict();

/** The commission calculator of the form (old R-3, CashTransfers.tsx:362-425) computed by the server (ADR-02 §2.9). */
export const transferQuoteSchema = z
  .object({
    currency: z.string().regex(/^[A-Z0-9]{3,8}$/),
    direction: z.enum(["out", "in"]),
    route: z.enum(["vault", "partner"]),
    amount: decimalSchema,
    partnerFee: decimalSchema.optional(),
    commissionPayer: z.enum(["partner", "beneficiary"]).optional(),
    /** Exactly one source of the commission: the rate (per 1,000,000 IQD / per 1,000 units), the commission itself, or the typed total. */
    commissionRate: decimalSchema.optional(),
    commission: decimalSchema.optional(),
    total: decimalSchema.optional(),
  })
  .strict()
  .refine((v) => (v.commissionRate !== undefined ? 1 : 0) + (v.commission !== undefined ? 1 : 0) + (v.total !== undefined ? 1 : 0) <= 1, { message: "ONE_COMMISSION_SOURCE" });
export type TransferQuoteInput = z.infer<typeof transferQuoteSchema>;

/** D-086: cancel = one reversal entry; `delete_movements` + own PIN + a written reason; no permanent delete. */
export const transferCancelSchema = z.object({ transferId: uuidSchema, reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema }).strict();
/** D-147: every print / PDF / share of the voucher is audited; a reprint is a copy. */
export const transferPrintSchema = z.object({ channel: z.enum(["print", "pdf", "whatsapp"]) }).strict();

// ---------------------------------------------------------------------------
// [C24] Journal (سجل القيود, D06 part 1): list, cancel / bulk cancel, edit, undo (D-091 – D-096, D-221; D-251 – D-253)
// ---------------------------------------------------------------------------

/** The old type filter of the journal (`App.tsx:28703-28714`), mapped to operation types on the server (D-252 (b)). */
export const JOURNAL_CATEGORIES = ["all", "deposit", "withdraw", "transfer", "product", "debt", "installment", "employee", "expense", "manual_work"] as const;
export type JournalCategory = (typeof JOURNAL_CATEGORIES)[number];
/** The old sortable columns (`App.tsx:29216-29306`). */
export const JOURNAL_SORTS = ["item", "c_in", "c_out", "comm", "created_at"] as const;

const journalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const journalQuerySchema = z
  .object({
    from: journalDateSchema.optional(), // office dates (D-096)
    to: journalDateSchema.optional(),
    hour: z.string().regex(/^([01]\d|2[0-3])$/).optional(), // office time zone (D-096; old: local clock)
    category: z.enum(JOURNAL_CATEGORIES).default("all"),
    search: z.string().trim().max(100).optional(),
    operatorUserId: uuidSchema.optional(),
    sort: z.enum(JOURNAL_SORTS).default("created_at"),
    order: z.enum(["asc", "desc"]).default("desc"),
    page: z.coerce.number().int().min(1).max(100_000).default(1),
    pageSize: z.coerce.number().int().refine((n) => [10, 20, 50, 100].includes(n), "PAGE_SIZE").default(20),
    /** D-095: the exports take the whole filtered set (capped). */
    all: z.enum(["0", "1"]).optional(),
    /** Audit & Control (B5): cancelled operations and edit groups instead of the visible journal. */
    view: z.enum(["journal", "corrections"]).default("journal"),
  })
  .strict();
export type JournalQuery = z.infer<typeof journalQuerySchema>;

/** D-014 exception: the Super Manager's username + PIN on the same screen (or the caller's own PIN when the caller is the Super Manager). */
const negativeOverrideSchema = {
  allowNegative: z.array(uuidSchema).max(50).optional(),
  negativeApproval: supervisorSchema.optional(),
};

/** D-091 / D-094: one or many operations, one reason, all-or-nothing; `delete_movements` + own PIN. */
export const journalCancelSchema = z
  .object({ operationIds: z.array(uuidSchema).min(1).max(200), reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema, ...negativeOverrideSchema })
  .strict();
export type JournalCancelInput = z.infer<typeof journalCancelSchema>;

/** D-092: the corrected business input of the same operation type (only the editable fields may differ). */
export const journalEditPreviewSchema = z.object({ operationId: uuidSchema, input: z.record(z.string(), z.unknown()), reason: z.string().trim().min(1).max(500) }).strict();
export const journalEditCommitSchema = z.object({ previewId: uuidSchema, idempotencyKey: uuidSchema, pin: pinSchema, ...negativeOverrideSchema }).strict();

/** D-093: undo of a cancel (the cancelled operation) or of an edit (the corrected entry); `undo_movements` + own PIN + reason. */
export const journalUndoSchema = z.object({ operationId: uuidSchema, reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema, ...negativeOverrideSchema }).strict();

// ---------------------------------------------------------------------------
// [C25] Profits & Expenses (الأرباح والمصاريف, D06 part 2; D-098 – D-102, D-254 – D-256)
// ---------------------------------------------------------------------------

/** The old period selector (App.tsx:31851-32083): day, month, year, custom range, all data; office dates (D-098). */
export const PROFIT_PERIODS = ["day", "month", "year", "custom", "all"] as const;
export type ProfitPeriodKind = (typeof PROFIT_PERIODS)[number];

/**
 * `GET /profits`: one period for both tabs (the old selector set the profit and expense filters together,
 * App.tsx:31856-31859); the search and log-date filters of each tab narrow that tab's list, summaries and total only
 * (the header cards ignore them, as `currentPeriodStats` did, App.tsx:10273-10358).
 */
/** A calendar date that exists (`2026-02-31` and `2026-13-01` are refused before they reach PostgreSQL). */
const realDateSchema = journalDateSchema.refine((d) => {
  const [y, m, day] = d.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, day));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === day;
}, "DATE_INVALID");
export const profitsQuerySchema = z
  .object({
    period: z.enum(PROFIT_PERIODS).default("day"),
    day: realDateSchema.optional(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
    year: z.string().regex(/^\d{4}$/).optional(),
    from: realDateSchema.optional(),
    to: realDateSchema.optional(),
    profitSearch: z.string().trim().max(100).optional(),
    /** Operation types and profit categories whose **displayed** (localized) label matches `profitSearch` — the browser
     * holds the catalogue, the server matches them as well as its own texts (the old search read the item text). */
    profitMatch: z.string().max(2000).regex(/^[a-z0-9_,]*$/).optional(),
    profitDate: realDateSchema.optional(),
    expenseSearch: z.string().trim().max(100).optional(),
    expenseDate: realDateSchema.optional(),
  })
  .strict();
export type ProfitsQuery = z.infer<typeof profitsQuerySchema>;
